<template>
  <div class="anon-settings">
    <div class="grid">
      <div class="field">
        <label>Your anonymity</label>
        <div class="toggles">
          <label
            ><input
              type="checkbox"
              :checked="model.anon_name"
              @change="onToggle('anon_name', $event)"
            />
            Hide {{ whose }} name</label
          >
          <label
            ><input
              type="checkbox"
              :checked="model.anon_username"
              @change="onToggle('anon_username', $event)"
            />
            Hide {{ whose }} username</label
          >
          <label
            ><input
              type="checkbox"
              :checked="model.anon_photo"
              @change="onToggle('anon_photo', $event)"
            />
            Hide {{ whose }} photo</label
          >
        </div>
      </div>
      <div class="field">
        <label>Preview</label>
        <div class="preview-card">
          <div class="avatar">
            <img v-if="!model.anon_photo" :src="photoUrl || ponyAvatar()" alt="avatar" />
            <div v-else class="placeholder">Hidden</div>
          </div>
          <div class="who">
            <div class="line">
              <span class="label">Name:</span>
              <span>{{ model.anon_name ? 'Hidden' : fullName || 'Anonymous' }}</span>
            </div>
            <div class="line">
              <span class="label">Username:</span>
              <span>{{ model.anon_username ? 'Hidden' : username ? '@' + username : '—' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export interface ExampleUser {
  username?: string | null
  full_name?: string | null
  photo_url?: string | null
}

export const AnonUser: ExampleUser = {
  photo_url: ponyAvatar(),
  username: 'username',
  full_name: 'Example User',
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { ponyAvatar } from '@/utils/images'

export interface AnonModel {
  anon_username: boolean
  anon_name: boolean
  anon_photo: boolean
}

const props = defineProps<{
  modelValue: AnonModel
  whose: string
  user?: ExampleUser
}>()
const emit = defineEmits<{ (e: 'update:modelValue', v: AnonModel): void }>()

const model = computed(() => props.modelValue)

function onToggle<K extends keyof AnonModel>(key: K, e: Event) {
  const target = e.target as HTMLInputElement
  emit('update:modelValue', { ...model.value, [key]: target.checked })
}

const auth = useAuthStore()
const photoUrl = computed<string | null>(
  () => props?.user?.photo_url ?? auth.payload?.tg?.photo_url ?? null,
)
const username = computed<string | null>(
  () => props?.user?.username ?? auth.payload?.tg?.username ?? null,
)
const fullName = computed<string | null>(() => {
  if (props?.user?.full_name) {
    return props.user.full_name
  }
  const tg = auth.payload?.tg
  if (!tg) return null
  const parts = [tg.first_name, tg.last_name].filter(Boolean)
  return parts.length ? parts.join(' ') : null
})
</script>

<style scoped lang="scss">
.anon-settings {
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1rem;
    margin-top: 0.5rem;
  }

  .field {
    border: 1px solid var(--color-border);
    border-radius: 0.6rem;
    padding: 0.75rem;
    background: var(--color-background-soft);
  }

  label {
    font-weight: 700;
    display: block;
    margin-bottom: 0.4rem;
  }

  .toggles {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .preview-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--color-background);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid var(--color-border);
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar .placeholder {
    font-size: 0.8rem;
    opacity: 0.7;
  }

  .who {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .line {
    display: flex;
    gap: 0.35rem;
  }

  .label {
    font-weight: 600;
    opacity: 0.8;
  }
}
</style>
